<div class="login-background">
  <div class="logo-container">
    <img src="logo.png" alt="Logo" class="logo" />
  </div>

  @if (
    !forgotPasswordFlow &&
    !confirmForgotPasswordFlow &&
    !resetPasswordFlow &&
    !mfaRequired &&
    !mfaSetup &&
    !mfaVerify &&
    !showEmailConfirmationPrompt &&
    !showConfirmCodeInput
  ) {
    <div class="login-container">
      <div class="header">
        <div class="header-text">
          <h1>Enter log in details</h1>
          <p>Let’s begin a life-saving journey!</p>
        </div>
      </div>
      <div class="login-card">
        @if (this.isLoading) {
          <div class="spinner-container">
            <div class="spinner"></div>
          </div>
        } @else {
          <div>
            <p class="failedLoginText">{{ this.errorMessage }}</p>
          </div>
          <form [formGroup]="loginForm" (ngSubmit)="onSubmit()">
            <!-- Email Field -->
            <div class="form-group">
              <label for="email">Email*</label>
              <input
                id="email"
                type="email"
                class="form-control"
                formControlName="email"
                placeholder="Enter your email"
                [ngClass]="{
                  'is-invalid':
                    loginForm.get('email')?.invalid &&
                    loginForm.get('email')?.dirty,
                }"
              />
              @if (
                loginForm.get("email")?.invalid && loginForm.get("email")?.dirty
              ) {
                <small class="text-danger"
                  >Please enter a valid email address.</small
                >
              }
            </div>

            <!-- Password Field -->
            <div class="form-group">
              <label for="password">Password*</label>
              <div class="passwordInput">
                <input
                  id="password"
                  [type]="showPassword ? 'text' : 'password'"
                  class="form-control"
                  formControlName="password"
                  placeholder="Enter your password"
                  autocomplete="off"
                  [ngClass]="{
                    'is-invalid':
                      loginForm.get('password')?.invalid &&
                      loginForm.get('password')?.dirty,
                  }"
                />
                @if (loginForm.get("password")?.valid) {
                  <i
                    class="toggle-password-icon bi"
                    [ngClass]="showPassword ? 'bi-eye-slash' : 'bi-eye'"
                    (click)="togglePasswordVisibility()"
                  ></i>
                }
              </div>

              @if (
                loginForm.get("password")?.hasError("required") &&
                loginForm.get("password")?.dirty
              ) {
                <small class="text-danger">Please enter your password.</small>
              }
              <div class="forgotPasswordText forgot-link">
                <a (click)="this.toggleForgotPassword()">Forgot password?</a>
              </div>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              class="btn btn-primary btn-block"
              [disabled]="loginForm.invalid"
            >
              Log in
            </button>
          </form>

          <p class="signup-link">
            Do not have an account? <a href="/signup">Sign up</a>
          </p>
        }
      </div>
    </div>
  } @else if (forgotPasswordFlow) {
    <div class="forgotPassword-container">
      <div class="header">
        <div class="header-text">
          <h1>Forgot your password?</h1>
          <p>No worries, we will help you reset it.</p>
        </div>
      </div>
      <div class="forgotPassword-card">
        @if (this.isLoading) {
          <div class="spinner-container">
            <div class="spinner"></div>
          </div>
        } @else {
          <div>
            <p class="failedLoginText">{{ this.errorMessage }}</p>
          </div>
          <form
            [formGroup]="forgotPasswordForm"
            (ngSubmit)="onForgotPassword()"
          >
            <!-- Email Field -->
            <div class="form-group">
              <label for="email">Email*</label>
              <input
                id="email"
                type="email"
                class="form-control"
                formControlName="email"
                placeholder="Enter your email"
                [ngClass]="{
                  'is-invalid':
                    forgotPasswordForm.get('email')?.invalid &&
                    forgotPasswordForm.get('email')?.dirty,
                }"
              />
              @if (
                forgotPasswordForm.get("email")?.invalid &&
                forgotPasswordForm.get("email")?.dirty
              ) {
                <small class="text-danger"
                  >Please enter a valid email address.</small
                >
              }
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              class="btn btn-primary btn-block"
              [disabled]="forgotPasswordForm.invalid"
            >
              Forgot password
            </button>
          </form>
        }
      </div>
      <div (click)="this.toggleForgotPassword()">
        <p class="backtoSignin-text">Back to sign in</p>
      </div>
    </div>
  } @else if (confirmForgotPasswordFlow) {
    <div class="forgotPassword-container">
      <div class="header">
        <div class="header-text">
          <h1>Reset your password</h1>
          <p>Enter the code sent to your email and choose a new password.</p>
        </div>
      </div>
      <div class="forgotPassword-card">
        @if (this.isLoading) {
          <div class="spinner-container">
            <div class="spinner"></div>
          </div>
        } @else {
          @if (!confirmPasswordSuccessful) {
            @if (this.errorMessage) {
              <div>
                <p class="failedLoginText">{{ this.errorMessage }}</p>
              </div>
            }

            <form
              [formGroup]="confirmForgotPasswordForm"
              (ngSubmit)="onConfirmForgotPassword()"
            >
              <!-- Email (Read-only) -->
              <div class="form-group">
                <label for="email">Email*</label>
                <input
                  id="email"
                  type="email"
                  class="form-control disabled-input"
                  formControlName="email"
                  [disabled]="true"
                  readonly
                />
              </div>

              <!-- Confirmation Code -->
              <div class="form-group">
                <label for="code">Confirmation Code*</label>
                <input
                  id="code"
                  type="text"
                  class="form-control"
                  formControlName="code"
                  placeholder="Enter confirmation code"
                  [ngClass]="{
                    'is-invalid':
                      confirmForgotPasswordForm.get('code')?.invalid &&
                      confirmForgotPasswordForm.get('code')?.dirty,
                  }"
                />
                @if (
                  confirmForgotPasswordForm.get("code")?.invalid &&
                  confirmForgotPasswordForm.get("code")?.dirty
                ) {
                  <small class="text-danger"
                    >Please enter your confirmation code.</small
                  >
                }
              </div>

              <!-- New Password -->
              <div class="form-group">
                <label for="newPassword">New Password*</label>
                <div class="passwordInput">
                  <input
                    id="newPassword"
                    [type]="showConfirmForgotPassword ? 'text' : 'password'"
                    class="form-control"
                    formControlName="newPassword"
                    autocomplete="off"
                    placeholder="Enter new password"
                    [ngClass]="{
                      'is-invalid':
                        confirmForgotPasswordForm.get('newPassword')?.invalid &&
                        confirmForgotPasswordForm.get('newPassword')?.dirty,
                    }"
                  />
                  @if (
                    confirmForgotPasswordForm.get("newPassword")?.touched &&
                    confirmForgotPasswordForm.get("newPassword")?.dirty
                  ) {
                    <i
                      class="toggle-password-icon bi"
                      [ngClass]="
                        showConfirmForgotPassword ? 'bi-eye-slash' : 'bi-eye'
                      "
                      (click)="toggleConfirmForgotPasswordVisibility()"
                    ></i>
                  }
                </div>

                @if (
                  confirmForgotPasswordForm.get("newPassword")?.invalid &&
                  confirmForgotPasswordForm.get("newPassword")?.touched
                ) {
                  <small
                    *ngIf="
                      confirmForgotPasswordForm.get('newPassword')?.errors?.[
                        'missingUppercase'
                      ] && confirmForgotPasswordForm.get('newPassword')?.touched
                    "
                    class="text-danger"
                  >
                    Password must contain at least one uppercase letter.
                  </small>
                  <small
                    *ngIf="
                      confirmForgotPasswordForm.get('newPassword')?.errors?.[
                        'missingSymbol'
                      ] && confirmForgotPasswordForm.get('newPassword')?.touched
                    "
                    class="text-danger"
                  >
                    Password must contain at least one symbol (e.g., #$%^&*).
                  </small>
                  <small
                    class="text-danger"
                    *ngIf="
                      confirmForgotPasswordForm.get('newPassword')?.invalid &&
                      confirmForgotPasswordForm.get('newPassword')?.touched
                    "
                  >
                    Password must be at least 8 characters long.
                  </small>
                }
              </div>

              <!-- Submit Button -->
              <button
                type="submit"
                class="btn btn-primary btn-block"
                [disabled]="confirmForgotPasswordForm.invalid"
              >
                Reset Password
              </button>
            </form>
          } @else {
            <div class="success-message-box">
              <div class="success-icon">
                <i class="bi bi-check-circle"></i>
              </div>
              <h3>Success</h3>
              <p>
                Password change successful!<br />
                Redirecting to login in {{ countdown }}s...
              </p>
            </div>
          }
        }
      </div>
    </div>
  } @else if (resetPasswordFlow) {
    <div class="reset-password-container">
      <div class="header">
        <div class="header-text">
          <h1>Reset password</h1>
          <p>Please create a new password to log in.</p>
        </div>
      </div>
      <div class="forgotPassword-card">
        @if (this.isLoading) {
          <div class="mini-spinner-container">
            <div class="mini-spinner"></div>
          </div>
        } @else {
          <form [formGroup]="resetPasswordForm" (ngSubmit)="onResetPassword()">
            <div *ngIf="errorMessage" class="error">{{ errorMessage }}</div>

            <!-- Current password field -->
            <div class="form-group">
              <label for="previousPassword">Current Password</label>
              <div class="passwordInput">
                <input
                  id="previousPassword"
                  [type]="showPassword ? 'text' : 'password'"
                  formControlName="previousPassword"
                  placeholder="Enter current password"
                  class="form-control"
                  autocomplete="off"
                  spellcheck="false"
                  [ngClass]="{
                    'is-invalid':
                      resetPasswordForm.get('previousPassword')?.invalid &&
                      resetPasswordForm.get('previousPassword')?.touched,
                  }"
                />
                @if (resetPasswordForm.get("previousPassword")?.dirty) {
                  <i
                    class="toggle-password-icon bi"
                    [ngClass]="showPassword ? 'bi-eye-slash' : 'bi-eye'"
                    (click)="togglePasswordVisibility()"
                  ></i>
                }
              </div>

              <small
                class="text-danger"
                *ngIf="
                  resetPasswordForm.get('previousPassword')?.invalid &&
                  resetPasswordForm.get('previousPassword')?.touched
                "
              >
                Current password is required.
              </small>
            </div>

            <!-- New password field -->
            <div class="form-group">
              <label for="newPassword">New Password</label>
              <div class="passwordInput">
                <input
                  id="newPassword"
                  [type]="showNewPassword ? 'text' : 'password'"
                  formControlName="newPassword"
                  placeholder="Enter new password"
                  class="form-control"
                  [ngClass]="{
                    'is-invalid':
                      resetPasswordForm.get('newPassword')?.invalid &&
                      resetPasswordForm.get('newPassword')?.touched,
                  }"
                />
                @if (resetPasswordForm.get("newPassword")?.dirty) {
                  <i
                    class="toggle-password-icon bi"
                    [ngClass]="showNewPassword ? 'bi-eye-slash' : 'bi-eye'"
                    (click)="toggleNewPasswordVisibility()"
                  ></i>
                }
              </div>

              <small
                *ngIf="
                  resetPasswordForm.get('newPassword')?.errors?.[
                    'missingUppercase'
                  ] && resetPasswordForm.get('newPassword')?.dirty
                "
                class="text-danger"
              >
                Password must contain at least one uppercase letter.
              </small>
              <small
                *ngIf="
                  resetPasswordForm.get('newPassword')?.errors?.[
                    'missingSymbol'
                  ] && resetPasswordForm.get('newPassword')?.dirty
                "
                class="text-danger"
              >
                Password must contain at least one symbol (e.g., #$%^&*).
              </small>
              <small
                class="text-danger"
                *ngIf="
                  resetPasswordForm.get('newPassword')?.invalid &&
                  resetPasswordForm.get('newPassword')?.dirty
                "
              >
                Password must be at least 8 characters long.
              </small>
            </div>

            <!-- Confirm Password Field -->
            <div class="form-group">
              <label for="confirmPassword">Confirm Password</label>
              <div class="passwordInput">
                <input
                  id="confirmPassword"
                  [type]="showConfirmPassword ? 'text' : 'password'"
                  formControlName="confirmPassword"
                  placeholder="Confirm new password"
                  class="form-control"
                  [ngClass]="{
                    'is-invalid':
                      resetPasswordForm.errors?.['passwordsMismatch'] &&
                      resetPasswordForm.get('confirmPassword')?.touched,
                  }"
                />
                @if (resetPasswordForm.get("confirmPassword")?.dirty) {
                  <i
                    class="toggle-password-icon bi"
                    [ngClass]="showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'"
                    (click)="toggleConfirmPasswordVisibility()"
                  ></i>
                }
              </div>
              <small
                class="text-danger"
                *ngIf="
                  resetPasswordForm.errors?.['passwordsMismatch'] &&
                  resetPasswordForm.get('confirmPassword')?.touched
                "
              >
                Passwords do not match.
              </small>
            </div>

            <button
              class="btn btn-primary btn-block"
              [disabled]="resetPasswordForm.invalid"
              type="submit"
            >
              Reset Password
            </button>
          </form>
        }
      </div>
    </div>
  } @else if (mfaRequired) {
    <div class="mfa-container">
      <div class="header">
        <div class="header-text">
          <h1>Enter Authentication Code</h1>
          <p>Please enter the code from your authentication app.</p>
        </div>
      </div>
      <div class="mfa-card">
        <!-- Spinner -->
        @if (isLoading) {
          <div class="spinner-container">
            <div class="spinner"></div>
          </div>
        } @else {
          <!-- MFA Form -->
          <form
            [formGroup]="mfaForm"
            class="mfa-form"
            autocomplete="off"
            (ngSubmit)="onSubmitMfa()"
          >
            <div class="form-group">
              <label for="code">Authentication Code</label>
              <input
                class="form-control"
                id="mfaCode"
                type="text"
                formControlName="mfaCode"
                maxlength="6"
                placeholder="Enter 6-digit code"
                #mfaRequiredField
                autocomplete="off"
                spellcheck="false"
                [ngClass]="{
                  'is-invalid':
                    mfaForm.get('mfaCode')?.invalid &&
                    mfaForm.get('mfaCode')?.dirty,
                }"
              />
              <small
                *ngIf="
                  mfaForm.get('mfaCode')?.invalid && mfaForm.get('mfaCode')?.dirty
                "
                class="error-message"
              >
                Please enter a valid 6-digit code.
              </small>
            </div>

            <!-- Error Message -->
            <div>
              <p class="failedLoginText">{{ this.errorMessage }}</p>
            </div>

            <button
              class="btn btn-primary btn-block"
              type="submit"
              [disabled]="mfaForm.invalid"
            >
              Verify Code
            </button>
          </form>
        }
      </div>
    </div>
  } @else if (mfaSetup) {
    <app-mfa-setup
      [qrCodeValue]="
        'otpauth://totp/Graft:' +
        mfaEmail +
        '?secret=' +
        mfaSecretCode +
        '&issuer=Graft'
      "
      [secretCode]="mfaSecretCode"
      (nextStep)="onMfaSetupComplete()"
    ></app-mfa-setup>
  } @else if (mfaVerify) {
    <div class="mfa-container">
      <div class="header">
        <div class="header-text">
          <h1>Verify Authentication Setup</h1>
          <p>
            Please enter the code from your authentication app to complete
            setup.
          </p>
        </div>
      </div>
      <div class="mfa-card">
        <!-- Spinner -->
        <div *ngIf="isLoading" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <!-- MFA Verification Form -->

        <form
          *ngIf="!isLoading"
          [formGroup]="mfaForm"
          (ngSubmit)="onSubmitMfaVerification()"
          class="mfa-form"
        >
          <div class="form-group">
            <label for="mfaCode">Authentication Code</label>
            <input
              id="mfaCode"
              type="text"
              formControlName="mfaCode"
              maxlength="6"
              placeholder="Enter 6-digit code"
              class="form-control"
              #mfaVerifyInput
              autocomplete="off"
              spellcheck="false"
              [ngClass]="{
                  'is-invalid':
                    mfaForm.get('mfaCode')?.invalid &&
                    mfaForm.get('mfaCode')?.dirty,
                }"
            />
            <small
              *ngIf="
                mfaForm.get('mfaCode')?.invalid &&
                mfaForm.get('mfaCode')?.dirty
              "
              class="error-message"
            >
              Please enter a valid 6-digit code.
            </small>
          </div>

          <!-- Error Message -->
          <div *ngIf="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
          @if (!mfaVerificationSuccessful) {
            <button
              type="submit"
              class="btn btn-primary btn-block"
              [disabled]="mfaForm.invalid"
            >
              Verify Code
            </button>
          }
        </form>
      </div>
    </div>
  } @else if (showEmailConfirmationPrompt) {
    <div class="resend-confirmation-container">
      <div class="header">
        <div class="header-text">
          <h1>Verify Email</h1>
          <p>
            Your email address is not confirmed, verify email to complete setup.
          </p>
        </div>
      </div>
      <form
        [formGroup]="verifyEmailForm"
        class="resend-confirmation-card verify-email-form"
      >
        <div class="form-group">
          <label for="verifyEmail">Confirm Email Address</label>
          <input
            id="verifyEmail"
            type="text"
            formControlName="email"
            placeholder="Email Address"
            class="form-control"
            [ngClass]="{
                  'is-invalid':
                    verifyEmailForm.get('email')?.invalid &&
                    verifyEmailForm.get('email')?.dirty,
                }"
          />
          <small
            *ngIf="
              verifyEmailForm.get('email')?.invalid &&
              verifyEmailForm.get('email')?.dirty
            "
            class="error-message"
          >
            Please enter a valid email address
          </small>
        </div>

        <!-- Error Message -->
        <div *ngIf="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
        <button
          type="button"
          class="btn btn-primary btn-block"
          [ngClass]="{
            'no-padding': isLoading,
          }"
          [disabled]="verifyEmailForm.invalid || isLoading"
          (click)="resendConfirmationEmail()"
        >
          @if (this.isLoading) {
            <div class="mini-spinner-container">
              <div class="mini-spinner"></div>
            </div>
          } @else {
            Resend Verfication Email
          }
        </button>
      </form>
    </div>
  } @else if (showConfirmCodeInput) {
    <div class="resend-confirmation-container">
      <div class="header">
        <div class="header-text">
          <h1>Verify Email</h1>
          <p>Please enter the code emailed to you to complete setup.</p>
        </div>
      </div>
      <div class="mfa-card">
        @if (!emailVerificationSuccessful) {
          <form [formGroup]="verifyEmailCodeForm" class="verify-email-form">
            <div class="form-group">
              <label for="verifyEmailCodeForm">Verification Code</label>
              <input
                id="verifyEmailCodeForm"
                type="text"
                formControlName="code"
                placeholder="Enter 6-digit code"
                class="form-control"
                [ngClass]="{
                  'is-invalid':
                    verifyEmailCodeForm.get('code')?.invalid &&
                    verifyEmailCodeForm.get('code')?.dirty,
                }"
              />
              
              <small
                *ngIf="
                  verifyEmailCodeForm.get('code')?.invalid &&
                  verifyEmailCodeForm.get('code')?.dirty
                "
                class="error-message"
              >
               Please enter a valid 6-digit code.
              </small>
            </div>

            <!-- Error Message -->
            <div *ngIf="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>
            <button
              class="btn btn-primary btn-block"
              [disabled]="verifyEmailCodeForm.invalid || isLoading"
              [ngClass]="{
                'no-padding': isLoading,
              }"
              (click)="verifyConfirmationCode()"
            >
              @if (this.isLoading) {
                <div class="mini-spinner-container">
                  <div class="mini-spinner"></div>
                </div>
              } @else {
                Verify Email
              }
            </button>
          </form>
        } @else {
          <div class="success-message-box">
            <div class="success-icon">
              <i class="bi bi-check-circle"></i>
            </div>
            <h3>Success</h3>
            <p>
              Verification successful!<br />
              Redirecting to login in {{ countdown }}s...
            </p>
          </div>
        }
      </div>
    </div>
  }
</div>
